import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

console.log('🔗 API Base URL:', API_BASE);

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
    timeout: 30000,
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        console.log(`📤 ${config.method.toUpperCase()} ${config.baseURL}${config.url}`);
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        console.log(`📥 ${response.status} ${response.config.url}`);
        return response;
    },
    (error) => {
        console.error(`❌ API Error: ${error.message}`);
        if (error.response) {
            console.error('Response status:', error.response.status);
            console.error('Response data:', error.response.data);
        }
        return Promise.reject(error);
    }
);

// Session APIs
export const createSession = (data) => api.post('/sessions/create', data);
export const startSession = (accessToken) => api.get(`/sessions/${accessToken}/start`);
export const saveResponse = (accessToken, data) => api.post(`/sessions/${accessToken}/response`, data);
export const submitAssessment = (accessToken) => api.post(`/sessions/${accessToken}/submit`);
export const addBlurEvent = (accessToken, timestamp) => api.post(`/sessions/${accessToken}/blur`, { timestamp });
export const getSessionStatus = (accessToken) => api.get(`/sessions/${accessToken}/status`);

// ============================================
// VIDEO SELECTION APIs (for EBCSS)
// ============================================
export const saveVideoSelection = (accessToken, data) => {
    console.log('📹 Saving video selection via API service:', data);
    return api.post(`/sessions/${accessToken}/video-selection`, data);
};

export const getVideoSelections = (accessToken, itemId) => {
    const url = itemId
        ? `/sessions/${accessToken}/video-selections?item_id=${itemId}`
        : `/sessions/${accessToken}/video-selections`;
    return api.get(url);
};

export const resetVideoSelections = (accessToken, itemId) => {
    return api.post(`/sessions/${accessToken}/reset-video-selection`, { item_id: itemId });
};

export const getFinalSelection = (accessToken, itemId) => {
    return api.get(`/sessions/${accessToken}/final-selection?item_id=${itemId}`);
};

export const markFinalSelection = (accessToken, data) => {
    return api.post(`/sessions/${accessToken}/mark-final`, data);
};

// Progress APIs - NEW
export const saveProgress = (accessToken, data) => api.post(`/sessions/${accessToken}/progress`, data);
export const getProgress = (accessToken) => api.get(`/sessions/${accessToken}/progress`);
export const resumeSession = (accessToken) => api.get(`/sessions/${accessToken}/resume`);

// ============================================
// FACE PHOTO APIs
// ============================================

/**
 * Capture a face photo for the session
 * @param {string} accessToken - The session access token
 * @param {number} photoOrder - The photo order (1, 2, or 3)
 * @param {string} image - Base64 encoded image data
 * @param {boolean} background - Whether this is a background capture
 * @returns {Promise} - API response
 */
export const captureFacePhoto = (accessToken, photoOrder, image, background = false) => {
    console.log(`📸 Capturing face photo ${photoOrder} via API service`);
    return api.post(`/face-photos/${accessToken}/capture`, {
        photo_order: photoOrder,
        image: image,
        background: background
    });
};

/**
 * Get all face photos for a session
 * @param {string} accessToken - The session access token
 * @returns {Promise} - API response with photos
 */
export const getFacePhotos = (accessToken) => {
    return api.get(`/face-photos/${accessToken}/photos`);
};

/**
 * Get face photo status for a session
 * @param {string} accessToken - The session access token
 * @returns {Promise} - API response with status
 */
export const getFacePhotoStatus = (accessToken) => {
    return api.get(`/face-photos/${accessToken}/status`);
};

/**
 * Check if a face photo capture is needed
 * @param {string} accessToken - The session access token
 * @param {number} photoOrder - The photo order to check (1, 2, or 3)
 * @returns {Promise} - API response
 */
export const checkFacePhotoNeeded = (accessToken, photoOrder) => {
    return api.get(`/face-photos/${accessToken}/check?photo_order=${photoOrder}`);
};

/**
 * Check and capture middle photo (photo 2) during assessment
 * @param {string} accessToken - The session access token
 * @param {string} image - Base64 encoded image data
 * @returns {Promise} - API response
 */
export const captureMiddlePhoto = (accessToken, image) => {
    return api.post(`/face-photos/${accessToken}/middle`, {
        image: image
    });
};

// Report APIs
export const getReport = (accessToken) => api.get(`/reports/${accessToken}`);
export const getAggregateReport = (assessmentId) => api.get(`/reports/aggregate/${assessmentId}`);

// Assessment APIs
export const getAssessments = () => api.get('/assessments');
export const getAssessment = (assessmentId) => api.get(`/assessments/${assessmentId}`);

export default api;